import { useState } from 'react';

export interface ConfirmModalProps {
  open: boolean;
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'default' | 'danger';
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
}

/**
 * Tiny modal for destructive / state-changing admin actions
 * (suspend, regenerate key). Tested via TenantTable.test.tsx.
 */
export function ConfirmModal(props: ConfirmModalProps) {
  const [busy, setBusy] = useState(false);
  if (!props.open) return null;
  const confirmCls = props.variant === 'danger' ? 'btn-danger' : 'btn-primary';
  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="confirm-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
    >
      <div className="card w-full max-w-md space-y-4">
        <h2 className="text-lg font-semibold">{props.title}</h2>
        <p className="text-sm text-slate-700">{props.description}</p>
        <div className="flex justify-end gap-2">
          <button type="button" className="btn" onClick={props.onCancel} disabled={busy}>
            {props.cancelLabel ?? 'Cancel'}
          </button>
          <button
            type="button"
            data-testid="confirm-modal-confirm"
            className={confirmCls}
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await props.onConfirm();
              } finally {
                setBusy(false);
              }
            }}
          >
            {props.confirmLabel ?? 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
}
